<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to Settings" @click="$router.push('/settings')">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="settings" pageId="face-ai" color="white"></help-link>
                </v-toolbar>
            </v-card>
            
            <v-alert :value="true" color="info" icon="info" outline class="mt-8" dismissible>
                To enable clock-in/clock-out with Facial Recognition for your employees, upload two recent 2x2 photos (ideally 1680x1680 pixels and around 500KB each) where the face is fully visible. Please ensure that there are no eyeglasses, hats, hair, or other coverings. 
            </v-alert>

             <v-alert :value="true" color="info" icon="info" outline class="mt-8" dismissible>
                Self-service activation is also available on the My Attendance > Face Recognition page. 
            </v-alert>

            <v-layout row wrap> 
                <v-flex xs12>
                    <v-card class="elavation-1 transparent">
                        <v-toolbar flat height="45">
                            <div :class="isMobile?'wp-50 mb-10':'wp-30 mb-10'">
                                <v-text-field v-model="search" append-icon="search" label="Search employee name" single-line hide-details></v-text-field>
                            </div>
                            <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-data-table
                            :headers="headers"
                            :items="employees_settings_filtered"
                            :loading="loading"  
                            :rows-per-page-items="rowsPerPageItems"
                            :pagination.sync="paginationSync1"
                        >
                            <template v-slot:items="props">
                                <tr @click="viewDetails(props.item)" title="Click to view details" class="clickable">
                                    <td>
                                        <v-chip class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.employee.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.employee.firstName}} {{props.item.employee.lastName}} 
                                        </v-chip>
                                    </td>
                                    <td>
                                        <span :class="props.item.status=='Active'?'green--text':'orange--text'">{{ props.item.status }}</span>
                                    </td>
                                    <td>
                                        {{ props.item.createdOn | moment('MMM DD, YYYY') }}
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
            </v-layout>

            <v-alert :value="true" color="info" icon="info" outline class="mt-8" dismissible>
                <b>Face AI Users</b> are users who are authorized to log in to the Face AI app and turn on Face Recognition attendance tracking on a mobile phone or tablet.
            </v-alert>
            
            <v-layout row wrap v-if="!loading"> 
                <v-flex xs12>
                    <v-card class="elavation-1 transparent">
                        <v-toolbar flat height="45">
                            <b class="primary--text">FACE AI USERS</b>
                            <v-spacer></v-spacer>
                            <v-btn v-if="hasAddRights" small fab class="primary" @click="showAddUser">
                                <v-icon>add</v-icon>
                            </v-btn>
                        </v-toolbar>
                        <v-data-table
                            :headers="header_authorized_users"
                            :items="authorized_users"
                            :loading="loading_users"  
                            :rows-per-page-items="rowsPerPageItems"
                            :pagination.sync="paginationSync2"
                        >
                            <template v-slot:items="props">
                                <tr>
                                    <td>
                                        <v-chip class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.user.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.user.firstName}} {{props.item.user.lastName}} 
                                        </v-chip>
                                    </td>
                                    <td>
                                        {{ props.item.createdOn | moment('MMM DD, YYYY') }}
                                    </td>
                                    <td>
                                        <v-btn v-if="hasDeleteRights" icon small title="Delete" @click="showDelete(props.item)">
                                            <v-icon small>delete</v-icon>
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-flex>
        
        <v-dialog v-model="dialogAddUser" width="550" persistent>   
            <add-user ref="addUserRef" :dialogAddUser.sync="dialogAddUser" @send="getAuthorizedUsers"></add-user>
        </v-dialog>
        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" :dialogDelete.sync="dialogDelete" @okDelete="deleteAuthorizeUser"></delete>
        </v-dialog>
    </v-layout> 
</template>

<script>

import faceAIApi from '@/api/faceai'
import awsS3Helper from '@/helpers/aws-s3'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'
import { hasAddRights, hasDeleteRights } from '@/helpers/access-permissions'

export default {     
    name: 'FaceAISettings',
    metaInfo: {
        title: 'Face AI Settings',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Face AI Settings' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-user': () => import('@/components/FaceRecognition/AddAuthorizedUser'),
        'delete': () => import('@/components/BasicPopups/Delete')
    },
    data () {
        const routeName1 = `${this.$route.name}-table1`
        const routeName2 = `${this.$route.name}-table2`
        const savedSort1 = userPrefs.getSort(routeName1)
        const savedSort2 = userPrefs.getSort(routeName2)

        return {
            awsS3Helper,
            rowsPerPageItems,
            paginationSync1: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName1),
                sortBy: savedSort1.sortBy,
                descending: savedSort1.descending,
            },
            paginationSync2: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName2),
                sortBy: savedSort2.sortBy,
                descending: savedSort2.descending,
            },
            search: userPrefs.getSearchKeyword(this.$route.name),
            error: false,
            errorMessage: '',
            loading: false,
            loading_users: false,
            loading_delete: false,
            dialogAddUser: false,
            dialogDelete: false,
            headers: [
                {text: 'Employee',align: 'left',value: 'lastName', width:'20%', sortable:true},
                {text: 'Status',align: 'left',value: 'status', width:'20%',sortable:true},
                {text: 'Activated On',align: 'left',value: '', width:'20%',sortable:false}
            ],
            header_authorized_users: [
                {text: 'Employee',align: 'left',value: 'lastName', width:'20%', sortable:true},
                {text: 'Added On',align: 'left',value: 'createdOn', width:'20%',sortable:true},
                {text: 'Action',align: 'left',value: '', width:'5%',sortable:false},
            ],
            descriptor: null,
            employees_settings: [],
            authorized_users: [],
            selectedUserIdForDelete: null,            
        }
    },
    methods: {
        showDelete(data) {
            this.$refs.refDeleteComponent.setMessage("Delete selected user?")
            this.selectedUserIdForDelete = data.user.userId
            this.dialogDelete = true
        },
        showAddUser() {
            this.$refs.addUserRef.setData(this.authorized_users)
            this.dialogAddUser = true
        },
        viewDetails(data) {
            this.$router.push(`/settings/faceai/${data.employee.userId}`)
        },
        async deleteAuthorizeUser() {
            try {
                const data = {
                    user : {
                        userId: this.selectedUserIdForDelete
                    }
                }
                this.loading_delete = true
                const response = await faceAIApi.removeAuthorizedUsers(data)
                if (response && response.status == 200) {
                    await this.getAuthorizedUsers()
                    this.dialogDelete = false
                }
                this.loading_delete = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getFaceAIStatus() {
            try {
                this.loading = true
                const response = await faceAIApi.getFaceAIStatus() 
                if (response && response.status == 200) {
                    this.employees_settings = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getAuthorizedUsers() {
            try {
                this.loading_users = true
                const response = await faceAIApi.getAuthorizedUsers() 
                if (response && response.status == 200) {
                    this.authorized_users = response.data.users 
                }
                this.loading_users = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    async created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: 'Face AI Activation',
            headerPageTitle: ''
        })

        await this.getFaceAIStatus()
        await this.getAuthorizedUsers()
    },
    watch: {
        'paginationSync1.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(`${this.$route.name}-table1`, newVal);
        },
        'paginationSync2.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(`${this.$route.name}-table2`, newVal);
        },
        'paginationSync1.sortBy'(newVal) {
            userPrefs.updateSort(`${this.$route.name}-table1`, newVal, this.paginationSync1.descending)
        },
        'paginationSync2.sortBy'(newVal) {
            userPrefs.updateSort(`${this.$route.name}-table2`, newVal, this.paginationSync2.descending)
        },
        'paginationSync1.descending'(newVal) {
            userPrefs.updateSort(`${this.$route.name}-table1`, this.paginationSync1.sortBy, newVal)
        },
        'paginationSync2.descending'(newVal) {
            userPrefs.updateSort(`${this.$route.name}-table2`, this.paginationSync2.sortBy, newVal)
        },
        search(newVal) {
            userPrefs.updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        hasDeleteRights() {
            return hasDeleteRights(this.$router.currentRoute.path)
        },
        employees_settings_filtered() {
            if (this.search) {
                return this.employees_settings.filter(item => {            
                    return item.employee.firstName.toLowerCase().includes(this.search.toLowerCase()) ||
                            item.employee.lastName.toLowerCase().includes(this.search.toLowerCase())
                })
            }
            return this.employees_settings
        },
        isMobile() {
            return window.innerWidth < 800
        },
    }
}
</script>